import { Link } from 'react-router-dom';
import { Card } from '~/components/ui';
import { EMPTY, formatNumber } from '~/lib/format';

interface StatTileProps {
  label: string;
  /** `null`/`undefined`/empty renders a dash. */
  value: string | number | null | undefined;
  /** Makes the whole tile a link to the page that fixes what it counts (DESIGN.md 7.4). */
  href?: string;
  /** Severity border and value color (DESIGN.md 7.2); the caller applies it only above its threshold. */
  severity?: 'warning' | 'error';
}

// Numbers (and plain numeric strings some APIs send) get locale digit grouping: 1522 → 1,522. Other text passes through.
const NUMERIC = /^-?\d+(\.\d+)?$/;
const display = (value: StatTileProps['value']) => {
  if (value === null || value === undefined || value === '' || value === 'Invalid date')
    return EMPTY;
  if (typeof value === 'number') return formatNumber(value);
  return NUMERIC.test(value) ? formatNumber(Number(value)) : value;
};

const BORDER = {
  warning: 'border-warning-300 dark:border-warning-500/40',
  error: 'border-error-300 dark:border-error-500/40',
};
const VALUE = {
  warning: 'text-warning-600 dark:text-warning-400',
  error: 'text-error-600 dark:text-error-400',
};

/** One headline number with its caption — the tile `StatsRow` lays out in a grid. */
export function StatTile({ label, value, href, severity }: StatTileProps) {
  const tile = (
    <Card
      className={`p-5 ${severity ? BORDER[severity] : ''} ${href ? 'transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.03]' : ''}`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p
        className={`mt-2 text-2xl font-semibold ${severity ? VALUE[severity] : 'text-gray-800 dark:text-white/90'}`}
      >
        {display(value)}
      </p>
    </Card>
  );
  if (!href) return tile;
  return (
    <Link
      to={href}
      className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
    >
      {tile}
    </Link>
  );
}
